<template>
  <BittsContainer class="api-key-item">
    <div class="api-key-item__header">
      <div class="api-key-item__title-block">
        <span class="api-key-item__name">{{ apiKey.name }}</span>
        <BittsBadge
          v-if="statusBadge"
          :status="statusBadge.status"
          :text="statusBadge.text"
          size="small"
        />
      </div>
      <BittsButton
        v-if="canWrite"
        type="danger"
        variant="ghost"
        size="small"
        :center-icon="['fas', 'trash-can']"
        aria-label="Revoke API key"
        data-testid="revoke-api-key-button"
        @click="isConfirmingRevoke = true"
      />
    </div>

    <dl class="api-key-item__meta">
      <ApiKeyItemMetaItem
        v-for="item in metaItems"
        :key="item.label"
        :label="item.label"
        :parts="item.parts"
        :is-numeric="item.isNumeric"
        :title="item.title"
        :subtitle="item.subtitle"
      />
    </dl>

    <div
      v-if="limits && limits.daily_limit > 0"
      :title="`${dailyUsagePercent}% of daily limit used today`"
    >
      <BittsProgressBar
        :width="`${Math.min(dailyUsagePercent, 100)}%`"
        :height="6"
        :color="usageBarColor"
      />
    </div>

    <BittsCollapse
      v-model:is-open="isPermissionsOpen"
      chevron-placement="right"
      aria-label="Permissions"
    >
      <template #title>
        <ApiKeyItemPermissionsSummary :api-key="apiKey" />
      </template>
      <!-- @click.stop because BittsCollapse toggles on any descendant click -->
      <div class="api-key-item__tag-rows" @click.stop>
        <ApiKeyItemTagRow
          v-for="row in tagRows"
          :key="row.label"
          :label="row.label"
          :items="row.items"
          :fallback="row.fallback"
          :color="row.color"
        />
      </div>
    </BittsCollapse>

    <BittsModal
      v-if="isConfirmingRevoke"
      v-model="isConfirmingRevoke"
      variant="confirm"
      :icon="['fas', 'trash-can']"
      icon-color="danger"
      title="Revoke API key?"
      :description="revokeConfirmDescription"
      primary-button-text="Revoke key"
      primary-button-type="danger"
      secondary-button-text="Cancel"
      :action-loading="isRevoking"
      @action="handleRevoke"
      @closed="isConfirmingRevoke = false"
    />
  </BittsContainer>
</template>

<script setup lang="ts">
import {
  BittsBadge,
  BittsButton,
  BittsCollapse,
  BittsContainer,
  BittsModal,
  BittsProgressBar,
} from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed, ref } from 'vue';

import ApiKeyItemMetaItem from '@/components/ApiKeyItemMetaItem.vue';
import ApiKeyItemPermissionsSummary from '@/components/ApiKeyItemPermissionsSummary.vue';
import ApiKeyItemTagRow from '@/components/ApiKeyItemTagRow.vue';

import { crossbeamApi } from '@/api';
import { useFlashesStore } from '@/stores';
import type { ApiKey, ApiKeyLimits } from '@/types/api_keys';
import { usageColorFor } from '@/utils/usageColor';

const props = defineProps<{
  apiKey: ApiKey;
  canWrite: boolean;
  limits?: ApiKeyLimits | null;
}>();
const emit = defineEmits<{ revoked: [id: string] }>();

const flashesStore = useFlashesStore();
const isRevoking = ref(false);
const isConfirmingRevoke = ref(false);
const isPermissionsOpen = ref(false);

const formattedCreatedAt = computed(() =>
  DateTime.fromISO(props.apiKey.created_at).toLocaleString(DateTime.DATE_MED),
);

function relativeTime(dateString: string): string {
  const rel = DateTime.fromISO(dateString).toRelative();
  if (!rel || rel.startsWith('in')) return 'just now';
  return rel;
}

const formattedLastUsedAt = computed(() => {
  if (!props.apiKey.last_used_at) return null;
  return DateTime.fromISO(props.apiKey.last_used_at).toLocaleString(
    DateTime.DATETIME_MED,
  );
});

const relativeLastUsed = computed(() => {
  if (!props.apiKey.last_used_at) return null;
  return relativeTime(props.apiKey.last_used_at);
});

const expiresAtUtc = computed(() => {
  if (!props.apiKey.expires_at) return null;
  return DateTime.fromISO(props.apiKey.expires_at, { zone: 'utc' });
});

const formattedExpiresAt = computed(
  () => expiresAtUtc.value?.toLocaleString(DateTime.DATE_MED) ?? null,
);

const isExpired = computed(() => {
  if (!expiresAtUtc.value) return false;
  return expiresAtUtc.value < DateTime.utc();
});

const daysUntilExpiration = computed(() => {
  if (!expiresAtUtc.value || isExpired.value) return null;
  const diff = expiresAtUtc.value
    .startOf('day')
    .diff(DateTime.utc().startOf('day'), 'days').days;
  return Math.max(0, Math.round(diff));
});

const statusBadge = computed<{
  status: 'danger' | 'warning';
  text: string;
} | null>(() => {
  if (isExpired.value) return { status: 'danger', text: 'Expired' };
  const days = daysUntilExpiration.value;
  if (days === null || days >= 7 || !expiresAtUtc.value) return null;
  const relative = expiresAtUtc.value.toRelativeCalendar({
    base: DateTime.utc(),
    unit: 'days',
  });
  return {
    status: 'warning',
    text: relative ? `Expires ${relative}` : 'Expires soon',
  };
});

const dailyUsagePercent = computed(() => {
  if (!props.limits?.daily_limit) return 0;
  return Math.round(
    ((props.apiKey.daily_usage_count ?? 0) / props.limits.daily_limit) * 100,
  );
});

const usageBarColor = computed(() => usageColorFor(dailyUsagePercent.value));

type MetaItem = {
  label: string;
  parts: { value: string; suffix?: string }[];
  isNumeric?: boolean;
  title?: string;
  subtitle?: string;
};

const metaItems = computed<MetaItem[]>(() => [
  {
    label: 'Created',
    parts: [{ value: formattedCreatedAt.value }],
    subtitle: `by ${props.apiKey.created_by_user_email}`,
  },
  {
    label: 'Last used',
    parts: [{ value: relativeLastUsed.value ?? 'Never' }],
    title: formattedLastUsedAt.value ?? undefined,
  },
  {
    label: isExpired.value ? 'Expired' : 'Expires',
    parts: [{ value: formattedExpiresAt.value ?? 'Never' }],
  },
  {
    label: 'Usage',
    isNumeric: true,
    parts: [
      {
        value: (props.apiKey.total_usage_count ?? 0).toLocaleString(),
        suffix: 'requests',
      },
      {
        value: (props.apiKey.daily_usage_count ?? 0).toLocaleString(),
        suffix: 'today',
      },
    ],
  },
]);

const tagRows = computed(() => [
  {
    label: 'Scopes',
    items: props.apiKey.scopes,
    fallback: 'No scopes',
    color: 'info',
  },
  {
    label: 'IPs',
    items: props.apiKey.allowed_ips ?? [],
    fallback: 'All IPs',
    color: 'neutral',
  },
  {
    label: 'Routes',
    items: props.apiKey.allowed_routes ?? [],
    fallback: 'All routes',
    color: 'neutral',
  },
]);

const revokeConfirmDescription = computed(
  () =>
    `"${props.apiKey.name}" will stop working immediately for any integration using it. This cannot be undone.`,
);

async function handleRevoke() {
  isRevoking.value = true;
  const { error } = await crossbeamApi.PUT('/v0.1/api-keys/{uuid}/revoke', {
    params: { path: { uuid: props.apiKey.id } },
  });
  if (error) {
    flashesStore.addErrorFlash({ message: 'Failed to revoke API key' });
    isConfirmingRevoke.value = false;
    isRevoking.value = false;
    return;
  }
  emit('revoked', props.apiKey.id);
  isRevoking.value = false;
}
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.api-key-item {
  @apply flex flex-col gap-16;
}

.api-key-item__header {
  @apply flex items-center justify-between gap-12;
}

.api-key-item__title-block {
  @apply flex items-center gap-8 flex-wrap min-w-0;
}

.api-key-item__name {
  @apply font-bold text-base text-neutral-text-strong truncate;
}

.api-key-item__meta {
  @apply grid grid-cols-2 gap-x-24 gap-y-8 m-0;
}

.api-key-item__tag-rows {
  @apply flex flex-col gap-8 pt-12;
}

:deep(.o-bitts-modal__header__image .icon-background) {
  @apply w-auto!;
}
</style>
